import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { EventCategory, EventSearchResult, SelectionRange, TimeRange, ViewportSelection } from "@Components/web-portal/byb360/match-timeline/types";
import {
  createPixelToTimeConverter,
  createTimeToPixelConverter,
  fullMatchCategory,
  generateCategoriesFromStandardEvents,
  generateRandomColor,
  TIMELINE_CONFIG,
} from "@Components/web-portal/byb360/match-timeline/utils";
import { StandardEventTagGroup, StandardMatchData, StandardMatchEvents } from "@Shared/types/TrackingTypes";

import { projectEvents } from "@hooks/index";
import { TimelineContext as ITimelineContext } from "./types";

const TimelineContext = createContext<ITimelineContext | null>(null);

interface TimelineProviderProps {
  children: React.ReactNode;
  matchEvents?: StandardMatchEvents; // New structure with events, categories, and tag groups
  metadata?: StandardMatchData; // Match metadata
  eventOffsets: {
    period1?: number | null;
    period2?: number | null;
  };
  projectId?: number; // Project ID for session storage
}

export const TimelineProvider: React.FC<TimelineProviderProps> = ({ children, matchEvents, metadata, eventOffsets, projectId }) => {
  // Extract events and other data from the new structure
  const events = matchEvents?.events;
  const eventCategories = matchEvents?.categories;
  const tagGroups = matchEvents?.tag_groups;
  const totalDuration = metadata ? (metadata.end_timestamp - metadata.start_timestamp) / 60 : 90;

  // Temporary: Convert malformed tag_groups to proper format for internal use
  const normalizedTagGroups = useMemo(() => {
    if (!tagGroups) return [];

    // delete null values
    const filteredTagGroups = tagGroups.filter((tagGroup) => tagGroup !== null);

    // Check if tagGroups is an array and has elements
    if (Array.isArray(filteredTagGroups) && filteredTagGroups.length > 0) {
      // If first element is an object with id and name properties (correct format)
      if (typeof filteredTagGroups[0] === "object" && "id" in filteredTagGroups[0] && "name" in filteredTagGroups[0]) {
        return filteredTagGroups as StandardEventTagGroup[];
      }

      // If first element is a string (malformed format - array of strings)
      if (typeof filteredTagGroups[0] === "string") {
        return (filteredTagGroups as unknown as string[]).map((tagGroup: string, index: number) => ({
          id: index,
          name: tagGroup,
          description: "",
        }));
      }
    }

    return [];
  }, [tagGroups]);

  // Generate categories and calculate total duration from StandardMatchEvents and metadata
  const dynamicCategories = useMemo(() => {
    // Use provided categories if available, otherwise generate from events
    let generatedCategories: EventCategory[] = [];
    if (eventCategories && eventCategories.length > 0) {
      // Convert StandardEventCategory to EventCategory and count events per category
      const eventCounts = new Map<number, number>();
      events?.forEach((event) => {
        eventCounts.set(event.category_id, (eventCounts.get(event.category_id) || 0) + 1);
      });

      const usedColors = new Set<string>();
      generatedCategories = eventCategories.map((cat) => {
        // Get color with fallback to random
        let color = cat.color;
        if (!color) {
          color = generateRandomColor();
          let attempts = 0;
          while (usedColors.has(color) && attempts < 20) {
            color = generateRandomColor();
            attempts++;
          }
        }
        usedColors.add(color);

        return {
          id: cat.id.toString(),
          name: cat.name,
          color,
          height: 60,
          visible: true,
          maxLayers: Math.min(10, Math.max(2, Math.ceil((eventCounts.get(cat.id) || 0) / 100))),
          eventCount: eventCounts.get(cat.id) || 0,
        };
      });
    } else if (events && events.length > 0) {
      // Fallback to generating categories from events (old behavior)
      generatedCategories = generateCategoriesFromStandardEvents(events);
    } else if (eventCategories?.length === 0 && events?.length === 0) {
      generatedCategories = [fullMatchCategory];
    }

    return generatedCategories;
  }, [events, eventCategories, metadata]);

  // Core timeline state
  const [timeRange, setTimeRange] = useState<TimeRange>({ start: 0, end: TIMELINE_CONFIG.DEFAULT_VIEWPORT_DURATION });
  const [selection, setSelection] = useState<SelectionRange | null>(null);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [timelineWidth, setTimelineWidth] = useState(800);
  const [categories, setCategories] = useState<EventCategory[]>(dynamicCategories);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<EventSearchResult[]>([]);

  // New state for tag filtering
  const [visibleTagIds, setVisibleTagIds] = useState<Set<number>>(new Set());

  // Event offset state (in seconds)
  const [period1EventOffset, setPeriod1EventOffset] = useState<number>(eventOffsets.period1 || 0);
  const [period2EventOffset, setPeriod2EventOffset] = useState<number>(eventOffsets.period2 || 0);
  const [shouldAutoOpenDialog, setShouldAutoOpenDialog] = useState<boolean>(false);

  const [secondHalfKickoffTime, setSecondHalfKickoffTime] = useState<number | null>(null); //in minutes from match start

  const getKickoffStorageKey = useCallback((projectId: number) => `byb360_kickoff_time_${projectId}`, []);

  useEffect(() => {
    if (projectId) {
      const storageKey = getKickoffStorageKey(projectId);
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = parseFloat(stored);
        if (!isNaN(parsed)) {
          setSecondHalfKickoffTime(parsed);
        }
      }
    }
  }, [projectId, getKickoffStorageKey]);

  const handleSetSecondHalfKickoffTime = useCallback(
    (time: number | null) => {
      setSecondHalfKickoffTime(time);
      if (projectId) {
        const storageKey = getKickoffStorageKey(projectId);
        if (time !== null) {
          localStorage.setItem(storageKey, time.toString());
        } else {
          localStorage.removeItem(storageKey);
        }
      }
    },
    [projectId, getKickoffStorageKey]
  );

  // Update categories when dynamic categories change
  useEffect(() => {
    setCategories(dynamicCategories);
  }, [dynamicCategories]);

  useEffect(() => {
    setPeriod1EventOffset(eventOffsets.period1 || 0);
    setPeriod2EventOffset(eventOffsets.period2 || 0);
  }, [eventOffsets.period1, eventOffsets.period2]);

  useEffect(() => {
    if (projectId && (eventOffsets.period1 || eventOffsets.period2)) {
      const storageKey = getKickoffStorageKey(projectId);
      const stored = localStorage.getItem(storageKey);

      if (!stored) {
        setShouldAutoOpenDialog(true);
      }
    }
  }, [projectId, eventOffsets.period1, eventOffsets.period2, getKickoffStorageKey]);

  // Initialize visible tags when tag groups change
  useEffect(() => {
    const allTagIds = new Set<number>();
    normalizedTagGroups.forEach((group) => {
      // Add the tag group ID (all tags in this group will be considered)
      allTagIds.add(group.id);
    });
    setVisibleTagIds(allTagIds);
  }, [normalizedTagGroups]);

  // Viewport selection state
  const [viewportSelection, setViewportSelection] = useState<ViewportSelection>({
    startTime: 0,
    endTime: Math.min(TIMELINE_CONFIG.DEFAULT_VIEWPORT_DURATION, totalDuration),
    isActive: true,
  });

  // Interaction state
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<number | null>(null);

  // Update timeRange when viewport selection changes
  useEffect(() => {
    setTimeRange({
      start: viewportSelection.startTime,
      end: viewportSelection.endTime,
    });
  }, [viewportSelection]);

  // Update viewport selection when total duration changes
  useEffect(() => {
    setViewportSelection((prev) => ({
      ...prev,
      endTime: Math.min(prev.endTime, totalDuration),
    }));
  }, [totalDuration]);

  // Derived state - visibleTimeRange is now just the timeRange since we removed zoomLevel
  const visibleTimeRange = useMemo(() => timeRange, [timeRange]);

  const visibleCategories = useMemo(() => categories.filter((cat) => cat.visible), [categories]);

  const applyEventOffset = useCallback(
    (event: any) => {
      let offset = 0;

      if (secondHalfKickoffTime !== null) {
        const kickoffTimeSeconds = secondHalfKickoffTime * 60;
        offset = event.start_time < kickoffTimeSeconds ? period1EventOffset : period2EventOffset;
      }

      return {
        ...event,
        start_time: event.start_time + offset,
        end_time: event.end_time + offset,
      };
    },
    [secondHalfKickoffTime, period1EventOffset, period2EventOffset]
  );

  // Filter events by category and tag visibility, applying period-specific offsets
  const visibleCategoryEvents = useMemo(
    () =>
      events
        ?.filter((event) => {
          const category = categories.find((cat) => cat.id === event.category_id.toString());
          if (!category?.visible) return false;

          if (visibleTagIds.size === 0) return true;

          return event.tags?.some((tag) => visibleTagIds.has(tag.group_id)) ?? true;
        })
        .map(applyEventOffset),
    [events, categories, visibleTagIds, applyEventOffset]
  );

  const selectedEvent = useMemo(() => {
    if (!selectedEventId || !events) return null;

    const event = events.find((e) => e.id.toString() === selectedEventId);
    if (!event) return null;

    return applyEventOffset(event);
  }, [selectedEventId, events, applyEventOffset]);

  // Conversion functions
  const timeToPixel = useMemo(() => createTimeToPixelConverter(visibleTimeRange, timelineWidth), [visibleTimeRange, timelineWidth]);

  const pixelToTime = useMemo(() => createPixelToTimeConverter(visibleTimeRange, timelineWidth), [visibleTimeRange, timelineWidth]);

  // Viewport actions
  const updateViewportSelection = useCallback((newSelection: ViewportSelection) => {
    setViewportSelection(newSelection);
  }, []);

  // Event handlers
  const handleZoom = useCallback(
    (direction: "in" | "out") => {
      const currentDuration = viewportSelection.endTime - viewportSelection.startTime;
      const center = viewportSelection.startTime + currentDuration / 2;

      let newDuration: number;
      if (direction === "in") {
        newDuration = currentDuration / TIMELINE_CONFIG.ZOOM_STEP;
      } else {
        newDuration = currentDuration * TIMELINE_CONFIG.ZOOM_STEP;
      }

      // Clamp the new duration to reasonable bounds
      const minDuration = TIMELINE_CONFIG.MIN_VIEWPORT_DURATION;
      const maxDuration = Math.min(TIMELINE_CONFIG.MAX_VIEWPORT_DURATION, totalDuration);
      newDuration = Math.max(minDuration, Math.min(newDuration, maxDuration));

      let newStartTime = center - newDuration / 2;
      let newEndTime = center + newDuration / 2;

      // Ensure we don't go outside the total duration bounds
      if (newStartTime < 0) {
        newStartTime = 0;
        newEndTime = newDuration;
      } else if (newEndTime > totalDuration) {
        newEndTime = totalDuration;
        newStartTime = totalDuration - newDuration;
      }

      updateViewportSelection({
        ...viewportSelection,
        startTime: newStartTime,
        endTime: newEndTime,
      });
    },
    [viewportSelection, totalDuration, updateViewportSelection]
  );

  const toggleCategoryVisibility = useCallback((categoryId: string) => {
    setCategories((prev) => prev.map((cat) => (cat.id === categoryId ? { ...cat, visible: !cat.visible } : cat)));
  }, []);

  // New function to toggle tag visibility
  const toggleTagVisibility = useCallback((tagId: number) => {
    setVisibleTagIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(tagId)) {
        newSet.delete(tagId);
      } else {
        newSet.add(tagId);
      }
      return newSet;
    });
  }, []);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const time = pixelToTime(x);
        setDragStart(time);
        setIsDragging(true);
        setSelection(null);
      }
    },
    [pixelToTime]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (isDragging && dragStart !== null) {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const time = pixelToTime(x);
        const endTime = Math.max(dragStart, time);
        setSelection({
          startTime: Math.min(dragStart, time),
          endTime,
        });
        projectEvents.emitSeek(endTime * 60);
      }
    },
    [isDragging, dragStart, pixelToTime]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setDragStart(null);
  }, []);

  const handleSearchResults = useCallback((results: EventSearchResult[], term: string) => {
    setSearchTerm(term);
    setSearchResults(results);
  }, []);

  const handleEventSelect = useCallback(
    (eventId: string) => {
      if (!events) return;

      setSelectedEventId(eventId);
      const event = events.find((e) => e.id.toString() === eventId);
      if (event) {
        const eventWithOffset = applyEventOffset(event);
        const startTime = eventWithOffset.start_time / 60;
        const endTime = eventWithOffset.end_time / 60;

        const thresholdS = 10 / 60;
        const finalEndTime = endTime - startTime < thresholdS ? startTime + thresholdS : endTime;
        setSelection({
          startTime,
          endTime: finalEndTime,
        });
      }
    },
    [events, secondHalfKickoffTime, period1EventOffset, period2EventOffset]
  );

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) {
      } else {
        switch (e.key) {
          case "Escape":
            setSelection(null);
            setSelectedEventId(null);
            break;
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleZoom]);

  const contextValue: ITimelineContext = {
    // State
    timeRange,
    selection,
    selectedEventId,
    timelineWidth,
    categories,
    events,
    isDragging,
    dragStart,
    viewportSelection,
    totalDuration,
    metadata,
    searchTerm,
    searchResults,

    // New state for tags
    tagGroups: normalizedTagGroups,
    visibleTagIds,
    eventCategories,

    // Event offset state
    period1EventOffset,
    period2EventOffset,
    secondHalfKickoffTime,
    shouldAutoOpenDialog,

    // Derived state
    visibleTimeRange,
    visibleCategories,
    visibleCategoryEvents,
    selectedEvent,

    // Conversion functions
    timeToPixel,
    pixelToTime,

    // Actions
    handleZoom,
    toggleCategoryVisibility,
    toggleTagVisibility,
    handleEventSelect,
    setSelection,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    setTimelineWidth,
    updateViewportSelection,
    handleSearchResults,
    setPeriod1EventOffset,
    setPeriod2EventOffset,
    setSecondHalfKickoffTime: handleSetSecondHalfKickoffTime,
    setShouldAutoOpenDialog,
  };

  return <TimelineContext.Provider value={contextValue}>{children}</TimelineContext.Provider>;
};

/**
 * Hook to access the timeline context
 */
export const useTimelineContext = (): ITimelineContext => {
  const context = useContext(TimelineContext);
  if (!context) {
    throw new Error("useTimelineContext must be used within a TimelineProvider");
  }
  return context;
};
